import { GiftCouponRedeemStatus } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftCsMemoLogItem } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { orderBrandName } from '@shared/utils/bugoBrandName.util';
import { giftDeliveryStatusLabel } from '@shared/utils/giftStatusLabel';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { redeemStatusLabel } from '../components/couponDetail/constants';
import {
  deliveryChannelLabel,
  dispatchInfo,
  orderTypeBadge,
  recipientInfo,
  shareProgress,
  shortOrderNo,
  targetCode,
  targetItemName,
} from './csMemoLogSummary';

// CS 응대 로그 엑셀 — 응대 매뉴얼·교육자료로 뽑아 쓰는 용도.
// 프로젝트 표준(커뮤니티 xlsx + file-saver)만 사용하고, 스타일 대신 너비·고정·필터로 읽히게 한다.

const fmt = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

const fmtD = (date: Date | string | undefined) => {
  if (!date) return '';
  const d = new Date(date);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const unitLabel = (item: GiftCsMemoLogItem) =>
  item.giftCoupon ? '쿠폰' : item.giftDelivery ? '배송' : '주문';

//* 상태 축 — 쿠폰 행은 발송(교환), 배송 행은 배송. 라벨이 겹쳐(`발송완료`/`배송완료`)
//* 피벗에서 섞이므로 값과 축을 **다른 열**로 내보낸다
const dispatchAxis = (item: GiftCsMemoLogItem) =>
  item.coupon ? '발송' : item.delivery ? '배송' : '';

//* 개발용 원문(Delivered/Shipping) 대신 운영 표기로 내보낸다 (응대 매뉴얼용 파일이므로)
const dispatchStatusLabel = (item: GiftCsMemoLogItem) => {
  if (item.coupon?.redeemStatus) {
    return (
      redeemStatusLabel[item.coupon.redeemStatus as GiftCouponRedeemStatus] ??
      item.coupon.redeemStatus
    );
  }
  if (item.delivery?.deliveryStatus) {
    return giftDeliveryStatusLabel(item.delivery.deliveryStatus);
  }
  return '';
};

export const exportCsMemoLogExcel = (
  items: GiftCsMemoLogItem[],
  range: { from: string; to: string },
) => {
  const rows = items.map((item, index) => {
    const dispatched = dispatchInfo(item);
    const shared = shareProgress(item);
    //* 쿠폰은 교환신청자, 배송은 건별 수령자 — 둘 다 주문자(상주)와 다른 사람이다
    const recipient = recipientInfo(item);
    return {
      //* 화면 표와 같은 규칙 — 최신 건이 가장 큰 번호 ("42번 건" 으로 서로 가리키기 위한 열)
      순번: items.length - index,
      작성일시: fmt(item.createdAt),
      작성자: item.byName,
      역할: item.byRole === 'Admin' ? '관리자' : '마리',
      공개범위: item.adminOnly ? '관리자 전용' : '공유',
      //* 같은 주문에 달린 메모끼리 엑셀에서 정렬·묶어보게 하는 키 (화면 표기와 같은 규칙)
      주문번호: shortOrderNo(item),
      응대건수: item.order?.memoCount ?? '',
      단위: unitLabel(item),
      //* 유형은 서버 order.itemTypes 로만 판정 — 메모 축으로 추론하면 혼합 주문에서 틀린다
      유형: orderTypeBadge(item)?.label ?? '',
      대상코드: targetCode(item),
      상품: targetItemName(item),
      상태구분: dispatchAxis(item),
      상태: dispatchStatusLabel(item),
      발송일자: dispatched ? fmtD(dispatched.date) : '',
      발송일구분: dispatched?.label ?? '',
      //* 매체마다 발신번호·알림 종류가 달라 "모르는 번호로 문자 왔다" 응대에 쓴다
      발송매체: deliveryChannelLabel(item.coupon?.deliveryChannel),
      //* "보냈는데 못 받았다"의 답은 발송이 아니라 공유·열람 쪽에 있다
      공유상태: shared?.label ?? '',
      공유일: shared?.date ? fmtD(shared.date) : '',
      발송취소횟수: item.coupon?.undeliverCount ?? '',
      택배사: item.delivery?.carrier ?? '',
      송장번호: item.delivery?.trackingNumber ?? '',
      //* 상조 — 필터·피벗의 1순위 축 (엑셀은 축약 없이 전체명)
      상조: orderBrandName(item.order),
      주문자: item.order?.receiverName ?? '',
      주문자연락처: item.order?.receiverPhoneNumber ?? '',
      //* 실제 받은 사람 — 주문자(상주)와 다르다.
      //* 쿠폰은 이름 없이 신청되는 건이 많아 구매자 메모가 이름 자리를 대신한다
      수령자메모: item.coupon?.memo ?? '',
      수령자: recipient?.name ?? '',
      수령자연락처: recipient?.phoneNumber ?? '',
      //* 축마다 뜻이 다른 일자 — 쿠폰은 교환신청, 배송은 배송지 제출(일괄배송은 없음).
      //* 발송일자/발송일구분과 같은 방식으로 값과 구분을 나눠 담는다
      수령일자: fmtD(recipient?.at?.date),
      수령일구분: recipient?.at?.label ?? '',
      결제일: fmtD(item.order?.purchasedAt),
      내용: item.body,
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 6 }, // 순번
    { wch: 18 }, // 작성일시
    { wch: 10 }, // 작성자
    { wch: 8 }, // 역할
    { wch: 12 }, // 공개범위
    { wch: 10 }, // 주문번호
    { wch: 8 }, // 응대건수
    { wch: 6 }, // 단위
    { wch: 8 }, // 유형
    { wch: 16 }, // 대상코드
    { wch: 24 }, // 상품
    { wch: 8 }, // 상태구분
    { wch: 12 }, // 상태
    { wch: 12 }, // 발송일자
    { wch: 10 }, // 발송일구분
    { wch: 12 }, // 발송매체
    { wch: 12 }, // 공유상태
    { wch: 12 }, // 공유일
    { wch: 12 }, // 발송취소횟수
    { wch: 12 }, // 택배사
    { wch: 18 }, // 송장번호
    { wch: 14 }, // 상조
    { wch: 10 }, // 주문자
    { wch: 15 }, // 주문자연락처
    { wch: 14 }, // 수령자메모
    { wch: 10 }, // 수령자
    { wch: 15 }, // 수령자연락처
    { wch: 12 }, // 수령일자
    { wch: 10 }, // 수령일구분
    { wch: 12 }, // 결제일
    { wch: 80 }, // 내용
  ];
  ws['!freeze'] = { xSplit: '0', ySplit: '1' };
  if (rows.length > 0) {
    //* 마지막 컬럼 인덱스는 !cols 길이에서 파생 — 컬럼을 늘릴 때 필터가 잘리는 일을 막는다
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rows.length, c: ws['!cols'].length - 1 },
      }),
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'CS응대로그');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `CS응대로그_${range.from}_${range.to}.xlsx`,
  );
};
